import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/Card";
import Image from "next/image";
import { StarIcon, ShoppingCart, Truck, Percent, Image as ImageIcon, Settings, Heart } from "lucide-react";
import Link from "next/link";
import { IProduct } from "@/lib/types/productType";
import { useCart } from "@/lib/stores/cartStore";
import { useState } from "react";

interface ProductCardProps {
  product: IProduct;
}

export default function ProductCard({ product }: ProductCardProps) {
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showFavoritesTooltip, setShowFavoritesTooltip] = useState(false);
  const { addItem } = useCart();

  const handleAddToFavorites = () => {
    setShowFavoritesTooltip(true);

    // Show success toast
    if (typeof window !== 'undefined' && (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast) {
      (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast!({
        type: "success",
        title: "Saved to Favorites",
        message: `${product.title} saved to favorites`,
        duration: 3000
      });
    }

    // Hide tooltip after 3 seconds
    setTimeout(() => {
      setShowFavoritesTooltip(false);
    }, 3000);
  };

  const handleAddToCart = async () => {
    console.log('🛒 Adding to cart:', { productId: product.id, productTitle: product.title });
    setIsAddingToCart(true);
    try {
      const cartItem = addItem(product.id, 1);
      console.log('🛒 Add to cart result:', cartItem);

      if (cartItem) {
        console.log(`✅ Added ${product.title} to cart successfully`);

        // Show success toast
        if (typeof window !== 'undefined' && (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast) {
          (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast!({
            type: "success",
            title: "Added to Cart",
            message: `${product.title} added successfully`,
            duration: 3000
          });
        }

        // Dispatch custom event to open cart sidebar with item ID for animation
        if (typeof window !== 'undefined') {
          console.log('📡 Dispatching cart-item-highlight event:', { itemId: cartItem.id, productTitle: product.title });
          window.dispatchEvent(new CustomEvent('cart-item-highlight', {
            detail: { itemId: cartItem.id, productTitle: product.title }
          }));
        }
      } else {
        console.error('❌ Failed to add item to cart - cartItem is null');

        // Show error toast
        if (typeof window !== 'undefined' && (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast) {
          (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast!({
            type: "error",
            title: "Failed to Add Item",
            message: "Please try again",
            duration: 4000
          });
        }
      }
    } catch (error) {
      console.error('❌ Error adding item to cart:', error);

      // Show error toast
      if (typeof window !== 'undefined' && (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast) {
        (window as Window & { addToast?: (toast: { type: string; title: string; message?: string; duration?: number }) => string }).addToast!({
          type: "error",
          title: "Error",
          message: "Something went wrong. Please try again.",
          duration: 4000
        });
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  const renderStars = (rating: number) => {
    // Ensure rating is a valid number between 0 and 5
    const validRating = Math.max(0, Math.min(5, isNaN(rating) ? 0 : rating));
    const fullStars = Math.floor(validRating);
    const hasHalfStar = validRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center space-x-1">
        {/* Full stars */}
        {[...Array(fullStars)].map((_, i) => (
          <StarIcon
            key={`full-${i}`}
            className="h-3 w-3 fill-yellow-400 text-yellow-400"
          />
        ))}

        {/* Half star */}
        {hasHalfStar && (
          <StarIcon className="h-3 w-3 fill-yellow-400/50 text-yellow-400" />
        )}

        {/* Empty stars */}
        {[...Array(emptyStars)].map((_, i) => (
          <StarIcon
            key={`empty-${i}`}
            className="h-3 w-3 text-gray-300"
          />
        ))}

        <span className="text-xs text-muted-foreground ml-1">
          ({validRating.toFixed(1)})
        </span>
      </div>
    );
  };

  return (
    <Card className="group hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-4">
        <Link href={`/product/${product.id}`}>
          <div className="aspect-square rounded-lg bg-gray-100 mb-3 overflow-hidden relative">
            <Image
              src={product.image}
              alt={product.title}
              width={300}
              height={300}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
            />

            {/* Tags overlay */}
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {product.hasFreeShipping && (
                <div className="bg-green-500 text-white border-2 border-gray-200 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                  <Truck className="w-3 h-3" />
                  Free Shipping
                </div>
              )}
              {product.isOnSale && (
                <div className="bg-red-500 text-white border-2 border-gray-200 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 min-w-[115px]">
                  <Percent className="w-3 h-3" />
                  Sale
                </div>
              )}
            </div>

            {/* Multiple images indicator */}
            {product.images && product.images.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {product.images.length}
              </div>
            )}

            {/* Variants indicator */}
            {product.variants && product.variants.length > 0 && (
              <div className="absolute bottom-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                <Settings className="w-3 h-3" />
                Options
              </div>
            )}
          </div>
        </Link>

        <div className="space-y-2">
          <Link href={`/product/${product.id}`}>
            <CardTitle className="text-sm font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
              {product.title}
            </CardTitle>
          </Link>

          <CardDescription className="flex text-xs text-gray-600 pt-2">
            <div className="flex flex-1/2 justify-start">
              {product.category.name}
            </div>
            <div className="flex flex-1/2 justify-end">
              {product.condition}
            </div>
          </CardDescription>

          {/* Product Specifications */}
          {product.specifications && product.specifications.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="space-y-1">
                {product.specifications.slice(0, 3).map((spec, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="text-gray-600">{spec.displayName}:</span>
                    <span className="font-medium text-gray-900">
                      {Array.isArray(spec.value)
                        ? spec.value.join(", ")
                        : spec.value
                      }
                    </span>
                  </div>
                ))}
                {product.specifications.length > 3 && (
                  <div className="text-xs text-gray-500 text-center pt-1">
                    +{product.specifications.length - 3} more specifications
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            {renderStars(product.rating)}
            <span className="text-xs text-muted-foreground">
              {product.reviewCount} reviews
            </span>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-green-600">
                {product.price}
              </span>
              {product.isOnSale && product.originalPrice && (
                <span className="text-sm text-gray-500 line-through">
                  {product.originalPrice}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              <Link
                href={`/stall/${product.stallId}`}
                className="hover:text-blue-600 transition-colors"
              >
                {product.stallName}
              </Link>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              className="flex-1 text-xs cursor-pointer"
              onClick={handleAddToCart}
              disabled={isAddingToCart}
            >
              <ShoppingCart className="h-3 w-3 mr-1" />
              {isAddingToCart ? 'Adding...' : 'Add to Cart'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-xs relative"
              onClick={handleAddToFavorites}
            >
              <Heart className="h-3 w-3 mr-1" />
              Save
              {showFavoritesTooltip && (
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  Saved to Favorites!
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-green-600"></div>
                </div>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
